import React, { useEffect, useState, useContext, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { Box, Divider, Flex, Grid, Label, Link, Spinner, Badge } from "theme-ui";
import { BackArrowIcon, PencilIcon, QuestionMarkIcon, TrashIcon, NotesIcon, OverviewIcon, VersionIcon } from "components/svg";
import H2 from "components/typography/H2";
import { themevals } from "theme/themevals";
import H5 from "components/typography/H5";
import Overview from "./components/Overview";
import Notes from "./components/Notes";
import CorrigoExportPanel from "./components/CorrigoExportPanel";
import UserContext from "context/User";
import { getMyCompany } from "api/Companies";
import { getQuote, acceptOrDeny } from "api/Quotes";
import { getClientWorkOrderContextForRfq } from "api/QuoteRequests";
import { trackActivity } from "api/ActivityTracker";
import { declineQuote, regenerateQuote, editQuoteVersion, getVersionHistory, lockPrice, voidLock, recordDeposit, getExpirationSettings, getQuoteDeclineReasons } from "api/QuoteExpiration";
import { useToast } from "components/toast";
import P from "components/typography/P";
import moment from "moment-timezone";
import BasicButton from "components/forms/BasicButton";
import BasicModal from "components/ui/BasicModal";
import DeclineReasonModal from "components/DeclineReasonModal";
import QuoteQuickEditModal from "components/QuoteQuickEditModal";
import QuoteVersionHistory from "components/QuoteVersionHistory";
import PriceLockModal from "components/PriceLockModal";
import RecordDepositModal from "components/RecordDepositModal";
import SiteMapSection from "components/SiteMapSection";
import SnapshotSelectionDisplay from "components/SnapshotSelectionDisplay";
import MaintenanceContextBanner from "components/MaintenanceContextBanner";

const Quote = () => {
  const [activeTab, setActiveTab] = useState<string>("overview");
  const { user } = useContext<any>(UserContext);
  const [company, setCompany] = useState<any>(null);
  const [quote, setQuote] = useState<any>(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const hasTrackedView = useRef(false);

  // Modal state
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [woContext, setWoContext] = useState<any>(null);
  const [showRegenModal, setShowRegenModal] = useState(false);
  const [showQuickEdit, setShowQuickEdit] = useState(false);
  const [showLockModal, setShowLockModal] = useState(false);
  const [lockModalInitialStep, setLockModalInitialStep] = useState<"choose" | "pay">("choose");
  const [showRecordDeposit, setShowRecordDeposit] = useState(false);
  const [showVoidLock, setShowVoidLock] = useState(false);
  const [showConfirmProject, setShowConfirmProject] = useState(false);
  const [confirmProjectSubmitting, setConfirmProjectSubmitting] = useState(false);
  const [expirationSettings, setExpirationSettings] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [prevQuote, setPrevQuote] = useState<any>(null);
  const [latestQuote, setLatestQuote] = useState<any>(null);
  const [declineReasons, setDeclineReasons] = useState<any[]>([]);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Quote";
    _init();
  }, [id]);

  /**
   * Deep-link support for `?tab=corrigo`. The maintenance work queue's
   * "Post to Corrigo" / "Record acceptance" actions link straight to the
   * step, so landing on the Overview and making the operator find the
   * tab would defeat the point. Runs once per quote id.
   */
  useEffect(() => {
    const requested = new URLSearchParams(window.location.search).get("tab");
    if (requested === "corrigo" || requested === "notes" || requested === "overview") {
      setActiveTab(requested);
    }
  }, [id]);

  const _init = async () => {
    if (user.role !== null) {
      const res = await getMyCompany();
      if (res.id) {
        setCompany(res);
      }
    } else {
      setCompany(user.company);
    }

    // Fetch expiration settings for price lock modal
    const settingsRes = await getExpirationSettings();
    if (settingsRes?.priceLockDepositPercent) {
      setExpirationSettings(settingsRes);
    }

    const quoteRes = await getQuote({ data: { id } });
    if (quoteRes.id) {
      // If superseded, find the latest version for the informational banner (no redirect)
      if (quoteRes.status === 5 && quoteRes.quoteRequest?.id) {
        try {
          const versions = await getVersionHistory(quoteRes.quoteRequest.id);
          if (Array.isArray(versions) && versions.length > 1) {
            const latest = versions[versions.length - 1];
            if (latest.id !== quoteRes.id) {
              setLatestQuote(latest);
            } else {
              setLatestQuote(null);
            }
          } else {
            setLatestQuote(null);
          }
        } catch (e) {
          setLatestQuote(null);
        }
      } else {
        setLatestQuote(null);
      }

      setQuote(quoteRes);

      // Maintenance back-link. A quote that came from a work order is a
      // dead end for a CLIENT: the quote page is a trunk surface with no
      // plan context, so the only way back to the work order they came
      // from is the browser button. Client-safe endpoint (no NTE, no
      // batch, no SLA); returns null for a plain RFQ, so nothing renders
      // for the ordinary case.
      if (quoteRes.quoteRequest?.id) {
        try {
          const ctx = await getClientWorkOrderContextForRfq({ rfqId: quoteRes.quoteRequest.id });
          setWoContext(ctx?.workOrderId ? ctx : null);
        } catch (e) {
          setWoContext(null);
        }
      } else {
        setWoContext(null);
      }

      // Fetch decline reasons for denied quotes
      if (quoteRes.status === 2) {
        try {
          const reasons = await getQuoteDeclineReasons(quoteRes.id);
          setDeclineReasons(Array.isArray(reasons) ? reasons : []);
        } catch (e: any) {
          setDeclineReasons([]);
        }
      } else {
        setDeclineReasons([]);
      }
      if (quoteRes.regeneratedFromQuoteId) {
        try {
          const prevRes = await getQuote({ data: { id: quoteRes.regeneratedFromQuoteId } });
          if (prevRes.id) {
            setPrevQuote(prevRes);
          }
        } catch (e) {
          // Predecessor may not be accessible — ignore
        }
      } else {
        setPrevQuote(null);
      }

      if (!hasTrackedView.current) {
        hasTrackedView.current = true;
        void trackActivity({
          module: "quotes",
          action: "view_quote_detail",
          actionType: "view",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "quote",
          entityId: quoteRes.id,
          metadata: {},
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }
    }
  };

  const resetQuote = (q: any) => {
    setQuote(q);
  };

  const handleDecline = async (data: { category: string | null; notes?: string; competitorName?: string }) => {
    if (!quote?.id) return;
    setSubmitting(true);
    const res = await declineQuote(quote.id, data);
    if (res.success) {
      toast.add("Quote declined.", "var(--theme-ui-colors-success)");
      setShowDeclineModal(false);
      _init(); // reload
    } else {
      toast.add(res.message || "Something went wrong.", "var(--theme-ui-colors-danger)");
    }
    setSubmitting(false);
  };

  const handleRegenerate = async () => {
    if (!quote?.id) return;
    setSubmitting(true);
    const res = await regenerateQuote(quote.id);
    if (res.success && res.newQuoteId) {
      toast.add(res.message || "Quote regenerated.", "var(--theme-ui-colors-success)");
      setShowRegenModal(false);
      navigate("/quotes/" + res.newQuoteId);
    } else {
      toast.add(res.message || "Something went wrong.", "var(--theme-ui-colors-danger)");
    }
    setSubmitting(false);
  };

  const handleQuickEdit = async (data: any) => {
    if (!quote?.id) return;
    setSubmitting(true);
    const res = await editQuoteVersion(quote.id, data);
    if (res?.id) {
      toast.add("New version created.", "var(--theme-ui-colors-success)");
      setShowQuickEdit(false);
      navigate("/quotes/" + res.id);
    } else {
      toast.add(res?.message || "Failed to create new version.", "var(--theme-ui-colors-danger)");
    }
    setSubmitting(false);
  };

  // Determine which actions are available
  const isActive = quote?.status === 0;
  const isExpired = quote?.status === 4;
  const isDraft = quote?.status === 3;
  const isPriceLocked = quote?.status === 6;
  // N2: price-lock vocabulary belongs to the price-lock FEATURE — a
  // customer who deliberately chose to hold a price. A deposit-flagged
  // client never chose that; the lock is only the mechanism the deposit
  // rides on. So their banners read as deposit, not as a feature they
  // did not ask for. Everyone else keeps the real wording.
  const clientRequiresDeposit =
    quote?.client?.requiresDeposit === true ||
    quote?.primaryClient?.requiresDeposit === true;
  const canDecline = isActive || isDraft || isExpired;
  const canRegenerate = isExpired || isActive;
  const isAdmin = user.role !== null;

  const handleLockPrice = async () => {
    setSubmitting(true);
    const res = await lockPrice(quote.id);
    setSubmitting(false);
    if (res?.success) {
      toast.add('Quote price locked.', 'success');
      setShowLockModal(false);
      _init();
    } else {
      toast.add(res?.message || 'Failed to lock price.', 'danger');
    }
  };

  // N2 deposit enforcement: a client flagged requiresDeposit does not
  // accept a quote straight to a project. Instead "Accept" locks the
  // price at the client's configured deposit percentage (default 50) and
  // drops them into the pay step, reusing the price-lock deposit rail.
  // Once the deposit is received, the existing status-6 "Confirm & Start
  // Project" flow creates the project (and the backend now auto-credits
  // the deposit onto the invoice). Fires only for deposit-flagged
  // clients on an active (status 0) quote; every other client is
  // unaffected.
  const handleRequireDeposit = async () => {
    const pct =
      ((quote?.requiresDeposit ? quote?.depositPercentage : null) ??
        quote?.client?.depositPercentage ??
        quote?.primaryClient?.depositPercentage ??
        50) as number;
    setSubmitting(true);
    const res = await lockPrice(quote.id, { depositPercent: pct });
    setSubmitting(false);
    if (res?.success) {
      await _init();
      // Open the pay step so the client pays the deposit now.
      setLockModalInitialStep("pay");
      setShowLockModal(true);
    } else {
      toast.add(res?.message || "Failed to set up deposit.", "danger");
    }
  };

  const handleVoidLock = async () => {
    setSubmitting(true);
    const res = await voidLock(quote.id);
    setSubmitting(false);
    if (res?.success) {
      toast.add(res.message || 'Price lock voided.', 'success');
      setShowVoidLock(false);
      _init();
    } else {
      toast.add(res?.message || 'Failed to void lock.', 'danger');
    }
  };

  const handleRecordDeposit = async (data: any) => {
    setSubmitting(true);
    const res = await recordDeposit(quote.id, data);
    setSubmitting(false);
    if (res?.success) {
      toast.add('Deposit recorded.', 'success');
      setShowRecordDeposit(false);
      _init();
    } else {
      toast.add(res?.message || 'Failed to record deposit.', 'danger');
    }
  };

  const handleConfirmProject = async () => {
    setConfirmProjectSubmitting(true);
    const res = await acceptOrDeny({ data: { id: quote.id }, update: { status: 1, discountFlag: false } });
    setConfirmProjectSubmitting(false);
    if (res?.quote) {
      toast.add(res.quote.project ? "Project created. Your deposit will be credited to the invoice." : "Quote accepted.", "var(--theme-ui-colors-success)");
      setShowConfirmProject(false);
      if (res.quote.project?.id) {
        navigate("/projects/" + res.quote.project.id);
      } else {
        _init();
      }
    } else {
      toast.add(res?.message || "Failed to confirm project.", "var(--theme-ui-colors-danger)");
    }
  };

  const versionSuffix = (quote?.majorVersion > 1 || quote?.minorVersion > 0)
    ? (quote?.minorVersion > 0 ? "-" + quote.majorVersion + "." + quote.minorVersion : "-" + quote.majorVersion)
    : "";

  // Helper: compute deposit deadline info from expiresAt (pending deposit state)
  const getDepositDeadlineInfo = () => {
    if (!quote?.expiresAt) return null;
    const deadlineMs = new Date(quote.expiresAt).getTime();
    const nowMs = Date.now();
    const daysLeft = Math.ceil((deadlineMs - nowMs) / 86400000);
    let color = "#276749"; // green
    let label = "green";
    if (daysLeft <= 3) {
      color = "#C53030"; // red
      label = "red";
    } else if (daysLeft <= 7) {
      color = "#C05621"; // orange
      label = "orange";
    }
    return { daysLeft, color, label, formattedDate: moment(quote.expiresAt).format("MMM D, YYYY") };
  };

  return (
    <>
      <Header></Header>
      <Page
        backAction={
          <Link variant="back" href="/quotes">
            <BackArrowIcon />
            {"Back to Quotes"}
          </Link>
        }
        $noPadding={true}
      >
        {quote == null ? (
          <Box sx={{ textAlign: "center", paddingTop: "30px" }}>
            <Spinner size={25} strokeWidth={3} title="loading" />
          </Box>
        ) : (
          <>
            <Grid gap={0} columns={[1, "250px 1fr"]}>
              <Box>
                <H5 marginTop={"30px"} marginBottom={"10px"} fontFamily={"Poppins"} color={themevals.colors.dkgrey} fontSize={"14px"} fontWeight={500}>
                  Quote Details
                </H5>
              </Box>
              <Flex sx={{ paddingLeft: 30, alignItems: "flex-end" }}>
                <Flex sx={{ gap: "40px" }}>
                  <Flex sx={{ paddingBottom: "10px", borderBottom: activeTab === "overview" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }} onClick={() => setActiveTab("overview")}>
                    <OverviewIcon fill={activeTab === "overview" ? "#000" : themevals.colors.dkgrey} width="14px" />
                    <Box sx={{ color: activeTab === "overview" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                      Overview
                    </Box>
                  </Flex>
                  <Flex sx={{ paddingBottom: "10px", borderBottom: activeTab === "notes" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }} onClick={() => setActiveTab("notes")}>
                    <NotesIcon fill={activeTab === "notes" ? "#000" : themevals.colors.dkgrey} width="14px" />
                    <Box sx={{ color: activeTab === "notes" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                      Notes
                    </Box>
                  </Flex>
                  {/*
                   * Corrigo export gets its OWN tab rather than riding
                   * above the Overview. For an LCG-shaped account this
                   * is a recurring job across 125 sites, not a one-off:
                   * it needs full width for twelve copy fields in strict
                   * order, and it should not sit in front of the quote
                   * details every time someone opens the record for an
                   * unrelated reason. Staff only, and only when the RFQ
                   * is plan-tagged (Corrigo-managed clients) — the ~284
                   * non-plan clients never see this tab.
                   */}
                  {isAdmin && quote.quoteRequest?.servicePlanId && (
                    <Flex sx={{ paddingBottom: "10px", borderBottom: activeTab === "corrigo" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }} onClick={() => setActiveTab("corrigo")}>
                      <VersionIcon fill={activeTab === "corrigo" ? "#000" : themevals.colors.dkgrey} width="14px" />
                      <Box sx={{ color: activeTab === "corrigo" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                        Corrigo
                      </Box>
                    </Flex>
                  )}
                </Flex>
              </Flex>
            </Grid>
            <Grid gap={0} columns={[1, "250px 1fr"]}>
              {/* See Projects/view.tsx: minWidth 0 + overflowWrap on the
                  sidebar column so emails and URLs wrap inside 250px. */}
              <Box sx={{ borderTop: "1px solid", borderRight: "1px solid", borderColor: "inputOutline", minWidth: 0, overflowWrap: "anywhere" }}>
                {user.role !== null && quote.status !== 5 && (
                  <Box sx={{ paddingRight: "30px", paddingTop: "20px" }}>
                    <Link
                      href={"/quotes/" + quote.id + "/edit"}
                      sx={{
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                        width: "100%", padding: "10px 0",
                        border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px",
                        textDecoration: "none", color: themevals.colors.black, backgroundColor: "#fff",
                        fontFamily: "Poppins", fontSize: "13px", fontWeight: 500, cursor: "pointer",
                        "&:hover": { backgroundColor: "#F7F8FA", borderColor: themevals.colors.dkgrey },
                      }}
                    >
                      <PencilIcon height="13px" fill="currentColor" />
                      Edit
                    </Link>
                  </Box>
                )}
                <Box sx={{ paddingRight: "30px" }}>
                  <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                    Basic Info
                  </H5>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Project Name</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {quote.projectName ? quote.projectName : "Instant Estimate"}
                    </P>
                  </Flex>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Status</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {(() => {
                        switch (quote.status) {
                          case 1: return <Badge as="span" variant="success">Accepted</Badge>;
                          case 2: return <Badge as="span" variant="danger">Denied</Badge>;
                          case 3: return <Badge as="span" variant="ltgrey">Draft</Badge>;
                          case 4: return <Badge as="span" variant="orange">Expired</Badge>;
                          case 5: return <Badge as="span" variant="ltgrey">Superseded</Badge>;
                          case 6: return <Badge as="span" variant="info">Price Locked</Badge>;
                          default: return <Badge as="span" variant="warning">New</Badge>;
                        }
                      })()}
                    </P>
                  </Flex>
                  {(quote.expiresAt || quote.expiredAt) && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>
                        {quote.expiredAt ? "Expired On" : "Expires On"}
                      </Label>
                      <P fontFamily={"Poppins"} color={quote.expiredAt ? themevals.colors.danger : "#FF7F07"} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {moment(quote.expiredAt || quote.expiresAt).format("MMM D, YYYY")}
                      </P>
                    </Flex>
                  )}
                  {quote.status === 0 && quote.quoteRequest?.updatedAt && new Date(quote.quoteRequest.updatedAt) > new Date(quote.createdAt) && (
                    <Flex sx={{
                      backgroundColor: "#fff3cd",
                      border: "1px solid #ffc107",
                      borderRadius: "6px",
                      padding: "8px 12px",
                      fontFamily: "Poppins",
                      fontSize: "12px",
                      marginBottom: "20px",
                      color: "#856404",
                      fontWeight: "500",
                    }}>
                      RFQ details changed since this quote was generated. Consider regenerating.
                    </Flex>
                  )}
                  {/* Older quotes (24 in production, newest 2024-12) have no RFQ. */}
                  {quote.quoteRequest?.id && (
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>RFQ #</Label>
                    <Link
                      href={"/quote-requests/" + quote.quoteRequest.id}
                      sx={{
                        fontFamily: "Poppins", color: themevals.colors.black, fontSize: "14px", fontWeight: "500",
                        marginTop: "5px", marginBottom: "0px", textDecoration: "underline", cursor: "pointer",
                        display: "inline-block", "&:hover": { color: themevals.colors.black, textDecoration: "underline" },
                      }}
                    >
                      {quote.quoteRequest.sequence ? "GMR-" + String(quote.quoteRequest.sequence).padStart(5, "0") : "N/A"}
                    </Link>
                  </Flex>
                  )}
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Created At</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {moment(quote.createdAt).format("MMM D, YYYY")}
                    </P>
                  </Flex>
                  {/* Install-only: mulch supply preference, read from the RFQ.
                      Structured field only; the supply wording rides on the
                      install-only service description in the line-item table. */}
                  {quote.quoteRequest?.mulchSupplyPreference && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Mulch Supply</Label>
                      <Box sx={{ marginTop: "5px" }}>
                        <Badge as="span" variant="info">
                          {quote.quoteRequest.mulchSupplyPreference === "pickup" ? "Pickup" : "Delivery"}
                        </Badge>
                      </Box>
                    </Flex>
                  )}
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Last Updated</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {moment(quote.updatedAt).format("MMM D, YYYY")}
                    </P>
                  </Flex>
                  {quote.lastUpdateBy?.firstName && quote.lastUpdateBy?.lastName && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Updated By</Label>
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {quote.lastUpdateBy.firstName + " " + quote.lastUpdateBy.lastName}
                      </P>
                    </Flex>
                  )}
                </Box>

                {/* Version History */}
                {quote.quoteRequest?.id && (
                  <Box sx={{ px: 0, pb: 2, pr: "30px" }}>
                    <QuoteVersionHistory quoteRequestId={quote.quoteRequest.id} currentQuoteId={quote.id} />
                  </Box>
                )}

                <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                {quote.primaryClient && (
                  <>
                    <Box sx={{ paddingRight: "30px" }}>
                      <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                        {user.role !== null ? "Primary Client" : "Primary Contact"}
                      </H5>
                      <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                        <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Name</Label>
                        <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                          {quote.primaryClient.name}
                        </P>
                      </Flex>
                      <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                        <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Email</Label>
                        <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                          {quote.primaryClient.contactEmail}
                        </P>
                      </Flex>
                      {quote.primaryClient.phone && (
                        <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                          <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Phone</Label>
                          <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                            {quote.primaryClient.phone}
                          </P>
                        </Flex>
                      )}
                    </Box>
                    <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                  </>
                )}
                <Box sx={{ paddingRight: "30px" }}>
                  <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                    Site Info
                  </H5>
                  <Flex sx={{ flexDirection: "column", marginBottom: canDecline ? "20px" : "60px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Address</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {quote.location?.address?.line1 ? quote.location.address.line1 : ""}
                      {quote.location.address.line2 ? ", " + quote.location.address.line2 : ""}
                    </P>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {quote.location.address.city + ", " + quote.location.address.state}
                      {quote.location.address.postalCode !== "null" ? " " + quote.location.address.postalCode : ""}
                    </P>
                  </Flex>
                  {quote.siteContactName && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Site Contact Name</Label>
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {quote.siteContactName}
                      </P>
                    </Flex>
                  )}
                  {quote.siteContactEmail && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Site Contact Email</Label>
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {quote.siteContactEmail}
                      </P>
                    </Flex>
                  )}
                </Box>

                {quote.location?.id && (
                  <>
                    {/*
                     * Session 30: split the map section by viewer role.
                     * Admin sees the full SiteMapSection with attach
                     * controls. Client (non-admin) sees only the
                     * SnapshotSelectionDisplay, and only when snapshots
                     * are attached — otherwise the whole section
                     * (including divider) disappears. No orphan divider,
                     * no "Open map" 403 button, no metadata card for a
                     * map with no snapshots.
                     */}
                    {isAdmin && (
                      <>
                        <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                        <SiteMapSection
                          locationId={quote.location.id}
                          mulchMapId={quote.mulchMapId ?? null}
                          recordType="quote"
                          recordId={quote.id}
                          onAttachChanged={() => _init()}
                        />
                      </>
                    )}
                    {!isAdmin && (quote.mulchMapSnapshotIds?.length ?? 0) > 0 && (
                      <>
                        <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                        <SnapshotSelectionDisplay
                          recordType="quote"
                          recordId={quote.id}
                          heading="Site maps included with this quote"
                        />
                      </>
                    )}
                  </>
                )}

                {/* Danger Zone — Decline for active/draft/expired quotes */}
                {canDecline && (
                  <Box sx={{ paddingBottom: "60px", paddingRight: "30px" }}>
                    <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                    <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                      Danger Zone
                    </H5>
                    <Flex sx={{ flexDirection: "column", gap: "8px" }}>
                      <BasicButton
                        scheme="dangerSecondary"
                        styles={{ margin: "0" }}
                        type="button"
                        onClick={() => setShowDeclineModal(true)}
                        $iconHeight="14px"
                        $iconWidth="14px"
                        keepText={true}
                      >
                        <TrashIcon fill="#A60000" />
                        Decline Quote
                      </BasicButton>
                    </Flex>
                  </Box>
                )}
              </Box>
              {company && (
                <Box sx={{ paddingTop: "30px", paddingLeft: "30px", borderTop: "1px solid", borderColor: "inputOutline" }}>
                  {/* A15 — consistent "back to work order" banner for a
                      quote that a Work Order spawned. Self-fetches from
                      the quote's RFQ; renders nothing for a plain quote
                      or a client viewer. */}
                  {isAdmin && (
                    <MaintenanceContextBanner rfqId={quote.quoteRequest?.id ?? null} />
                  )}
                  {/* Session 78 — the CLIENT half of the same idea. A
                      client who arrived here from their work order had no
                      route back: this page is a trunk surface with no plan
                      context, so the browser button was the only way. Own
                      endpoint, own (narrower) payload — work order and plan
                      only, no NTE, no batch, no SLA. Renders nothing for an
                      ordinary quote. */}
                  {!isAdmin && woContext && (
                    <Box
                      sx={{
                        backgroundColor: "#F7FAFC",
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                        padding: "12px 20px",
                        marginBottom: "20px",
                        fontFamily: "Poppins, sans-serif",
                      }}
                    >
                      <Box sx={{ fontSize: "12px", color: themevals.colors.dkgrey, marginBottom: "6px" }}>
                        {woContext.planName
                          ? `This quote is for work on your ${woContext.planName} plan.`
                          : "This quote is for work you asked us about."}
                      </Box>
                      <Flex sx={{ alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                        <Box
                          onClick={() => navigate(`/maintenance/work-orders/${woContext.workOrderId}`)}
                          sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: themevals.colors.primary,
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                        >
                          {woContext.externalId
                            ? `\u2039 Back to work order ${woContext.externalId}`
                            : "\u2039 Back to the work order"}
                        </Box>
                        {woContext.planId && (
                          <Box
                            onClick={() => navigate(`/maintenance/plans/${woContext.planId}`)}
                            sx={{
                              fontSize: "12px",
                              color: themevals.colors.primary,
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                          >
                            {woContext.planName ? `View ${woContext.planName}` : "View the plan"}
                          </Box>
                        )}
                      </Flex>
                    </Box>
                  )}
                  {/* Superseded banner — view only, no redirect */}
                  {quote.status === 5 && latestQuote && (
                    <Flex sx={{
                      backgroundColor: "#F7FAFC",
                      border: "1px solid #E2E8F0",
                      borderRadius: "8px",
                      padding: "12px 20px",
                      marginBottom: "16px",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "16px",
                    }}>
                      <Box>
                        <P fontFamily="Poppins" color={themevals.colors.black} fontSize="13px" fontWeight={600} marginTop="0px" marginBottom="2px">
                          This version has been superseded
                        </P>
                        <P fontFamily="Poppins" color={themevals.colors.dkgrey} fontSize="12px" fontWeight={400} marginTop="0px" marginBottom="0px">
                          {"A newer version (v" + latestQuote.majorVersion + (latestQuote.minorVersion > 0 ? "." + latestQuote.minorVersion : "") + ") is now the current quote."}
                        </P>
                      </Box>
                      <BasicButton
                        scheme="secondary"
                        outline={true}
                        styles={{ margin: "0", flexShrink: "0" }}
                        type="button"
                        onClick={() => navigate("/quotes/" + latestQuote.id)}
                      >
                        {"View Latest \u2192"}
                      </BasicButton>
                    </Flex>
                  )}
                  {/* Version comparison banner */}
                  {prevQuote && prevQuote.total != null && quote.total != null && (
                    (() => {
                      const prevTotal = prevQuote.total;
                      const currTotal = quote.total;
                      const diff = currTotal - prevTotal;
                      const pctChange = prevTotal > 0 ? (diff / prevTotal) * 100 : 0;
                      const prevMv = prevQuote.majorVersion || 1;
                      const prevMn = prevQuote.minorVersion || 0;
                      const prevLabel = "v" + prevMv + (prevMn > 0 ? "." + prevMn : "");
                      const currMv = quote.majorVersion || 1;
                      const currMn = quote.minorVersion || 0;
                      const currLabel = "v" + currMv + (currMn > 0 ? "." + currMn : "");
                      const went_up = diff > 0;
                      const went_down = diff < 0;
                      const borderColor = went_down ? "#48BB78" : went_up ? "#E2E8F0" : "#E2E8F0";
                      const bgColor = went_down ? "#F0FFF4" : "#F7FAFC";
                      const diffColor = went_down ? "#276749" : went_up ? "#C53030" : themevals.colors.dkgrey;
                      const fmtCurr = (v: number) => "$" + Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                      const sign = diff > 0 ? "+" : diff < 0 ? "-" : "";
                      return (
                        <Flex sx={{
                          backgroundColor: bgColor,
                          border: "1px solid " + borderColor,
                          borderRadius: "8px",
                          padding: "12px 20px",
                          marginBottom: "16px",
                          alignItems: "center",
                          gap: "16px",
                          flexWrap: "wrap",
                        }}>
                          <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "12px", color: themevals.colors.dkgrey }}>
                            Repriced from {prevLabel}
                          </Box>
                          <Flex sx={{ alignItems: "center", gap: "8px" }}>
                            <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "14px", fontWeight: 500, color: themevals.colors.dkgrey, textDecoration: "line-through" }}>
                              {fmtCurr(prevTotal)}
                            </Box>
                            <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "14px", color: themevals.colors.dkgrey }}>
                              {"\u2192"}
                            </Box>
                            <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "14px", fontWeight: 600, color: themevals.colors.black }}>
                              {fmtCurr(currTotal)}
                            </Box>
                          </Flex>
                          {diff !== 0 && (
                            <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "12px", fontWeight: 600, color: diffColor }}>
                              {sign}{fmtCurr(Math.abs(diff))} ({sign}{Math.abs(pctChange).toFixed(1)}%)
                            </Box>
                          )}
                        </Flex>
                      );
                    })()
                  )}

                  {/* ============================================================
                      DECLINE REASON BANNER
                      Red-tinted banner when quote is denied (status 2) with reasons.
                      ============================================================ */}
                  {quote.status === 2 && isAdmin && declineReasons.length > 0 && (
                    <div style={{
                      backgroundColor: "#FFF5F5",
                      border: "1px solid #FC8181",
                      borderRadius: "8px",
                      padding: "16px 20px",
                      marginBottom: "24px",
                      fontFamily: "Poppins, sans-serif",
                    }}>
                      <div style={{ fontSize: "14px", fontWeight: 600, color: "#C53030", marginBottom: "8px" }}>
                        {"Quote Denied"}
                      </div>
                      {declineReasons.map((reason: any, idx: number) => (
                        <div key={idx} style={{ fontSize: "12px", color: "#742A2A", lineHeight: "1.6" }}>
                          <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", marginBottom: "4px" }}>
                            {reason.category && (
                              <span>{"Reason: "}<strong>{reason.category.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())}</strong></span>
                            )}
                            {reason.competitorName && (
                              <span>{"Competitor: "}<strong>{reason.competitorName}</strong></span>
                            )}
                            {reason.submittedBy && (
                              <span>{"By: "}<strong>{(reason.submittedBy.firstName || "") + " " + (reason.submittedBy.lastName || "")}</strong></span>
                            )}
                            {reason.createdAt && (
                              <span>{"On: "}<strong>{moment(reason.createdAt).format("MMM D, YYYY")}</strong></span>
                            )}
                          </div>
                          {reason.notes && (
                            <div style={{ marginTop: "4px", fontStyle: "italic", color: "#9B2C2C" }}>
                              {'"' + reason.notes + '"'}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ============================================================
                      PRICE LOCK BANNER — PENDING DEPOSIT STATE
                      Teal banner when status=6 and deposit not yet received.
                      Shows deposit amount, deadline countdown, payment options.
                      ============================================================ */}
                  {isPriceLocked && quote.priceLockDepositStatus !== "received" && (() => {
                    const deadline = getDepositDeadlineInfo();
                    const depositAmt = Number(quote.priceLockDepositAmount || 0);
                    const fmtDeposit = "$" + depositAmt.toLocaleString("en-US", { minimumFractionDigits: 2 });
                    return (
                      <div style={{
                        backgroundColor: "#E6FFFA",
                        border: "1px solid #38B2AC",
                        borderRadius: "8px",
                        padding: "16px 20px",
                        marginBottom: "24px",
                        fontFamily: "Poppins, sans-serif",
                      }}>
                        {/* Header row */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" }}>
                          <div>
                            <div style={{ fontSize: "14px", fontWeight: 600, color: "#234E52", marginBottom: "4px" }}>
                              {clientRequiresDeposit ? "Deposit Required" : "Price Locked \u2014 Deposit Required"}
                            </div>
                            <div style={{ fontSize: "12px", color: "#285E61", lineHeight: "1.5" }}>
                              {depositAmt > 0 && deadline ? (
                                <>
                                  {"Pay your "}
                                  <strong>{fmtDeposit}</strong>
                                  {" deposit by "}
                                  <strong>{deadline.formattedDate}</strong>
                                  {" "}
                                  {deadline.daysLeft > 0 ? (
                                    <span style={{ color: deadline.color, fontWeight: 600 }}>
                                      {"(" + deadline.daysLeft + " day" + (deadline.daysLeft === 1 ? "" : "s") + " left)"}
                                    </span>
                                  ) : (
                                    <span style={{ color: "#C53030", fontWeight: 600 }}>{"(overdue \u2014 lock will expire)"}</span>
                                  )}
                                  {clientRequiresDeposit ? " to get this on the schedule." : " to keep this price locked."}
                                </>
                              ) : (
                                clientRequiresDeposit
                                  ? "Deposit pending. Submit payment to get this on the schedule."
                                  : "Deposit pending. Submit payment to secure your price lock."
                              )}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                            <BasicButton
                              scheme="primary"
                              styles={{ margin: "0", padding: "6px 14px", fontSize: "12px" }}
                              type="button"
                              onClick={() => { setLockModalInitialStep("pay"); setShowLockModal(true); }}
                              keepText={true}
                            >
                              Pay Now
                            </BasicButton>
                            {isAdmin && (
                              <BasicButton
                                scheme="secondary"
                                outline={true}
                                styles={{ margin: "0", padding: "6px 14px", fontSize: "12px" }}
                                type="button"
                                onClick={() => setShowVoidLock(true)}
                                keepText={true}
                              >
                                Void Lock
                              </BasicButton>
                            )}
                          </div>
                        </div>

                        {/* Deposit amount summary row */}
                        {depositAmt > 0 && (
                          <div style={{ marginTop: "12px", display: "flex", gap: "24px", fontSize: "12px", color: "#285E61" }}>
                            <span>{"Deposit: "}<strong style={{ color: "#234E52" }}>{fmtDeposit}</strong></span>
                            <span>{"Status: "}<strong style={{ color: "#C05621" }}>{"Pending"}</strong></span>
                          </div>
                        )}


                      </div>
                    );
                  })()}

                  {/* ============================================================
                      PRICE LOCK BANNER — SECURED STATE
                      Blue banner when status=6 and deposit has been received.
                      Shows 180-day countdown from priceLockExpiresAt, deposit details.
                      ============================================================ */}
                  {isPriceLocked && quote.priceLockDepositStatus === "received" && (
                    <div style={{
                      backgroundColor: "#EBF8FF",
                      border: "1px solid #3182CE",
                      borderRadius: "8px",
                      padding: "16px 20px",
                      marginBottom: "24px",
                      fontFamily: "Poppins, sans-serif",
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" }}>
                        <div>
                          <div style={{ fontSize: "14px", fontWeight: 600, color: "#000", marginBottom: "4px" }}>
                            {clientRequiresDeposit ? "Deposit Received \u2014 Ready to Proceed?" : "Price Locked \u2014 Ready to Proceed?"}
                            {quote.priceLockExpiresAt && (() => {
                              const daysLeft = Math.ceil((new Date(quote.priceLockExpiresAt).getTime() - Date.now()) / 86400000);
                              const countdownColor = daysLeft <= 7 ? "#C53030" : daysLeft <= 30 ? "#C05621" : "#276749";
                              return (
                                <span style={{ fontWeight: 500, fontSize: "13px", marginLeft: "8px" }}>
                                  {daysLeft > 0
                                    ? <span style={{ color: countdownColor }}>{daysLeft + " day" + (daysLeft === 1 ? "" : "s") + " remaining"}</span>
                                    : <span style={{ color: "#C53030" }}>Expired</span>
                                  }
                                  <span style={{ color: themevals.colors.dkgrey, fontSize: "12px", marginLeft: "6px" }}>
                                    {"(" + moment(quote.priceLockExpiresAt).format("MMM D, YYYY") + ")"}
                                  </span>
                                </span>
                              );
                            })()}
                          </div>
                          <div style={{ fontSize: "12px", color: themevals.colors.dkgrey }}>
                            {"Your $" + Number(quote.priceLockDepositAmount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 }) + " deposit secures this price" + (quote.priceLockExpiresAt ? " through " + moment(quote.priceLockExpiresAt).format("MMM D, YYYY") : "") + ". Confirm below to start your project."}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                          <BasicButton
                            scheme="primary"
                            styles={{ margin: "0", padding: "6px 14px", fontSize: "12px", backgroundColor: "#48BB78", borderColor: "#48BB78" }}
                            type="button"
                            onClick={() => setShowConfirmProject(true)}
                            keepText={true}
                          >
                            Confirm and Start Project
                          </BasicButton>
                          {isAdmin && (
                            <BasicButton
                              scheme="secondary"
                              outline={true}
                              styles={{ margin: "0", padding: "6px 14px", fontSize: "12px" }}
                              type="button"
                              onClick={() => setShowVoidLock(true)}
                              keepText={true}
                            >
                              Void Lock
                            </BasicButton>
                          )}
                        </div>
                      </div>
                      {quote.priceLockDepositAmount > 0 && (
                        <div style={{ marginTop: "12px", display: "flex", gap: "24px", fontSize: "12px", color: themevals.colors.dkgrey }}>
                          <span>{"Deposit: "}<strong style={{ color: themevals.colors.black }}>{"$" + Number(quote.priceLockDepositAmount).toLocaleString("en-US", { minimumFractionDigits: 2 })}</strong></span>
                          {quote.priceLockDepositMethod && <span>{"Method: "}<strong style={{ color: themevals.colors.black }}>{quote.priceLockDepositMethod}</strong></span>}
                          <span>{"Status: "}<strong style={{ color: "#276749" }}>{"Received"}</strong></span>
                          {quote.priceLockDepositPaidAt && <span>{"Paid: "}<strong style={{ color: themevals.colors.black }}>{moment(quote.priceLockDepositPaidAt).format("MMM D, YYYY")}</strong></span>}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Expired banner — informational only, Regenerate is in the button bar */}
                  {(isExpired || (isActive && quote.expiresAt && new Date(quote.expiresAt) < new Date())) && (
                    <Flex sx={{
                      backgroundColor: "#FFF8F0",
                      border: "1px solid #FF7F07",
                      borderRadius: "8px",
                      padding: "12px 20px",
                      marginBottom: "24px",
                      alignItems: "center",
                      gap: "16px",
                    }}>
                      <Box>
                        <P fontFamily="Poppins" color="#000" fontSize="14px" fontWeight={600} marginTop="0px" marginBottom="4px">
                          {isExpired
                            ? "This quote expired on " + moment(quote.expiredAt || quote.expiresAt).format("MMM D, YYYY")
                            : "This quote's expiration date has passed"}
                        </P>
                        <P fontFamily="Poppins" color={themevals.colors.dkgrey} fontSize="12px" fontWeight={400} marginTop="0px" marginBottom="0px">
                          Regenerate this quote to create a new version repriced at current rates.
                        </P>
                      </Box>
                    </Flex>
                  )}
                  {/*
                   * Maintenance Module §3.9 #17 — Corrigo paste-export.
                   * Lives in the MAIN column, not the 250px metadata
                   * sidebar it was originally mounted in: this is a
                   * twelve-field, copy-in-strict-order task, and at 250px
                   * every value clipped to "Certifie…" — you cannot paste
                   * a value you cannot see. It is also the reason a staff
                   * member opens a Corrigo quote at all, so it sits above
                   * the tab content rather than below the fold.
                   *
                   * Staff only, and only when the RFQ is plan-tagged
                   * (Corrigo-managed clients). The panel itself no-ops
                   * when there are no exportable lines, so untagged
                   * quotes and the ~284 non-plan clients see nothing.
                   */}
                  {isAdmin && quote.quoteRequest?.servicePlanId && activeTab === "corrigo" && (
                    <Box sx={{ marginBottom: "24px" }}>
                      <CorrigoExportPanel quote={quote} onAccepted={() => _init()} />
                    </Box>
                  )}

                  {activeTab === "overview" && <Overview isCompany={user.role !== null ? true : false} company={company} quote={quote} reloadQuote={resetQuote} onLockPrice={() => setShowLockModal(true)} onRequireDeposit={handleRequireDeposit} onRegenerate={() => setShowRegenModal(true)} />}
                  {activeTab === "notes" && <Notes isCompany={user.role !== null ? true : false} quote={quote} />}
                </Box>
              )}
            </Grid>
          </>
        )}
      </Page>

      {/* Decline Reason Modal */}
      {showDeclineModal && (
        <DeclineReasonModal
          type="quote"
          onConfirm={handleDecline}
          onCancel={() => setShowDeclineModal(false)}
          submitting={submitting}
        />
      )}

      {/* Regenerate Confirmation Modal */}
      {showRegenModal && (
        <BasicModal
          title="Regenerate Quote?"
          cancel_click={() => setShowRegenModal(false)}
          cancel_text="Cancel"
          size="sm"
          icon={<QuestionMarkIcon fill="#818181" height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="primary"
              onClick={handleRegenerate}
              $submitting={submitting}
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              Yes, Regenerate
            </BasicButton>
          }
        >
          <P>This will create a new version of this quote repriced at current rates. The expired quote will be preserved for reference.</P>
        </BasicModal>
      )}

      {/* Quick Edit Modal */}
      {showQuickEdit && quote && (
        <QuoteQuickEditModal
          quote={quote}
          onConfirm={handleQuickEdit}
          onCancel={() => setShowQuickEdit(false)}
          submitting={submitting}
        />
      )}

      {/* Price Lock Modal */}
      {showLockModal && quote && (
        <PriceLockModal
          quote={quote}
          settings={expirationSettings}
          initialStep={lockModalInitialStep}
          depositRequired={
            !!quote?.requiresDeposit ||
            quote?.client?.requiresDeposit === true ||
            quote?.primaryClient?.requiresDeposit === true
          }
          onComplete={() => { setShowLockModal(false); setLockModalInitialStep("choose"); _init(); }}
          onCancel={() => { setShowLockModal(false); setLockModalInitialStep("choose"); }}
        />
      )}

      {/* Record Deposit Modal */}
      {showRecordDeposit && quote && (
        <RecordDepositModal
          quote={quote}
          onConfirm={handleRecordDeposit}
          onCancel={() => setShowRecordDeposit(false)}
          submitting={submitting}
        />
      )}

      {/* Void Lock Confirmation */}
      {showVoidLock && (
        <BasicModal
          title="Void Price Lock?"
          cancel_click={() => setShowVoidLock(false)}
          cancel_text="Cancel"
          size="sm"
          icon={<QuestionMarkIcon fill="#818181" height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="danger"
              onClick={handleVoidLock}
              $submitting={submitting}
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              Yes, Void Lock
            </BasicButton>
          }
        >
          <P>{"This will remove the price lock and return the quote to active status." + (quote?.priceLockDepositStatus === "received" ? " The deposit will be retained as a client credit." : "")}</P>
        </BasicModal>
      )}

      {/* Confirm Project Modal (price-locked quotes with deposit received) */}
      {showConfirmProject && quote && (
        <BasicModal
          title="Confirm Project"
          cancel_click={() => setShowConfirmProject(false)}
          cancel_text="Cancel"
          size="sm"
          icon={<QuestionMarkIcon fill="#818181" height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="primary"
              onClick={handleConfirmProject}
              $submitting={confirmProjectSubmitting}
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0", backgroundColor: "#48BB78", borderColor: "#48BB78" }}
            >
              {"Confirm & Start Project"}
            </BasicButton>
          }
        >
          <div style={{ fontFamily: "Poppins, sans-serif" }}>
            <P lineHeight="22px" marginBottom="12px">
              {"You're confirming this quote at the locked price. Here's what happens next:"}
            </P>
            <div style={{ backgroundColor: "#F7FAFC", border: "1px solid #E2E8F0", borderRadius: "8px", padding: "12px 16px", marginBottom: "12px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "13px" }}>
                <span style={{ color: themevals.colors.dkgrey }}>Quote Total</span>
                <span style={{ fontWeight: 600 }}>{"$" + Number(quote.total || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "13px", color: "#276749" }}>
                <span>Deposit Paid</span>
                <span style={{ fontWeight: 600 }}>{"- $" + Number(quote.priceLockDepositAmount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", borderTop: "1px solid #E2E8F0", paddingTop: "6px", marginTop: "2px" }}>
                <span style={{ fontWeight: 600 }}>Remaining Balance</span>
                <span style={{ fontWeight: 700 }}>{"$" + Number((quote.total || 0) - (quote.priceLockDepositAmount || 0)).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
            <P lineHeight="20px" marginBottom="0px" fontSize="12px" color={themevals.colors.dkgrey}>
              A project and invoice will be created. Your deposit will be automatically credited to your invoice.
            </P>
          </div>
        </BasicModal>
      )}
    </>
  );
};

export default Quote;
